/* Sample data for first-time empty states.
 *
 * Single source of truth for the "Sample preview" experience surfaced on
 * every list (Seller Buyers, Orders, Invoices, Shipments; Buyer Orders,
 * Invoices, Shipments). All sample entities are interconnected — the
 * same Boutique Glow Co. that appears in the Buyers list is the buyer
 * on the sample Order, which produced the sample Invoice, which has a
 * sample Shipment. This gives new users a consistent narrative as they
 * click around instead of disconnected fragments.
 *
 * Hard rules:
 *   - These objects NEVER touch MongoDB. Pure client-side static data.
 *   - Every entity id is prefixed with `sample-` so any accidental
 *     `/api/...` call with one of these ids fails fast and obviously.
 *   - Visual treatment is always muted + "SAMPLE" badge so the user
 *     never confuses these with real entities.
 *   - Disappears the moment the real list has ≥ 1 entity.
 */

// ── Sample sellers (used on the buyer side) ────────────────────────────────
export const SAMPLE_SELLER = {
  id:           "sample-seller-1",
  name:         "Sarah Chen Naturals",
  slug:         "sarah-chen-naturals",
  account_url:  "sarahchen-1.verqo.com",
  brand_color:  "#5B7C99",
  category:     "Clean beauty · Skincare",
  rating:       4.8,
  reviews:      127,
};

// ── Sample buyers (interconnected with everything below) ──────────────────
export const SAMPLE_BUYERS = [
  {
    id:                 "sample-buyer-1",
    name:               "Boutique Glow Co.",
    email:              "ap@boutiqueglow.com",
    contact:            "Jordan Lee",
    location:           "Brooklyn, NY",
    verqo_score:        738,
    rating:             "Very Good",
    rating_color:       "emerald",
    status:             "connected",
    terms:              "Net-60",
    credit_limit:       25_000,
    credit_used:        12_400,
    open_ar:            4_200,
    last_order_at:      "2d ago",
    first_connected:    "Aug 2025",
    industry:           "Beauty boutique",
    locations_count:    3,
    annual_volume:      "$1.8M",
    insured:            true,
    insurance_carrier:  "Allianz Trade",
    notes:              "Reliable buyer — pays before due date 78% of the time.",
  },
  {
    id:                 "sample-buyer-2",
    name:               "Apex Retail Group",
    email:              "finance@apexretail.com",
    contact:            "Sam Reyes",
    location:           "Chicago, IL",
    verqo_score:        685,
    rating:             "Good",
    rating_color:       "emerald",
    status:             "connected",
    terms:              "Net-30",
    credit_limit:       50_000,
    credit_used:        28_600,
    open_ar:            12_800,
    last_order_at:      "1w ago",
    first_connected:    "Jun 2025",
    industry:           "Multi-store retailer",
    locations_count:    18,
    annual_volume:      "$14.2M",
    insured:            true,
    insurance_carrier:  "Allianz Trade",
    notes:              "Large account — uses their own UPS contract for shipping.",
  },
  {
    id:                 "sample-buyer-3",
    name:               "Modern Mercantile",
    email:              "orders@modernmerc.com",
    contact:            "Riley Okafor",
    location:           "Austin, TX",
    verqo_score:        540,
    rating:             "Building",
    rating_color:       "amber",
    status:             "invited",
    terms:              "Prepay",
    credit_limit:       0,
    credit_used:        0,
    open_ar:            0,
    last_order_at:      "—",
    first_connected:    "—",
    industry:           "Independent boutique",
    locations_count:    1,
    annual_volume:      "$420K",
    insured:            false,
    insurance_carrier:  null,
    notes:              "New buyer — limited history. Prepay-only until credit established.",
  },
];

// ── Sample orders (linked to the buyers above) ────────────────────────────
export const SAMPLE_ORDERS = [
  {
    id:             "sample-order-1",
    code:           "ORD-9001",
    buyer_id:       "sample-buyer-1",
    buyer_name:     "Boutique Glow Co.",
    seller_name:    "Sarah Chen Naturals",  // for buyer-side view
    status:         "delivered",
    stage:          "delivered",
    subtotal:       3_840,
    shipping_total: 360,
    total:          4_200,
    items_count:    24,
    units:          240,
    created_at:     "2d ago",
    invoice_code:   "INV-9001",
    invoice_status: "paid",
    tracking:       "1Z999AA10123456784",
    carrier:        "UPS",
    shipping_mode:  "verqo",
  },
  {
    id:             "sample-order-2",
    code:           "ORD-9002",
    buyer_id:       "sample-buyer-2",
    buyer_name:     "Apex Retail Group",
    seller_name:    "Sarah Chen Naturals",
    status:         "processing",
    stage:          "pickup",
    subtotal:       11_900,
    shipping_total: 900,
    total:          12_800,
    items_count:    52,
    units:          640,
    created_at:     "5d ago",
    invoice_code:   "INV-9002",
    invoice_status: "outstanding",
    tracking:       null,
    carrier:        null,
    shipping_mode:  "buyer_arranged",
  },
  {
    id:             "sample-order-3",
    code:           "ORD-9003",
    buyer_id:       "sample-buyer-1",
    buyer_name:     "Boutique Glow Co.",
    seller_name:    "Sarah Chen Naturals",
    status:         "in_transit",
    stage:          "transit",
    subtotal:       2_180,
    shipping_total: 145,
    total:          2_325,
    items_count:    18,
    units:          140,
    created_at:     "3d ago",
    invoice_code:   "INV-9003",
    invoice_status: "paid",
    tracking:       "9405511899223334445566",
    carrier:        "USPS",
    shipping_mode:  "seller",
  },
];

// ── Sample invoices (linked to the orders above) ──────────────────────────
export const SAMPLE_INVOICES = [
  {
    id:           "sample-invoice-1",
    code:         "INV-9001",
    order_id:     "sample-order-1",
    buyer_id:     "sample-buyer-1",
    buyer_name:   "Boutique Glow Co.",
    seller_id:    "sample-seller-1",
    seller_name:  "Sarah Chen Naturals",
    amount:       4_200,
    terms:        "prepaid",
    status:       "paid",
    due_date:     "2026-04-20T00:00:00Z",
    paid_at:      "2026-04-18T15:30:00Z",
    paid_method:  "ach",
    created_at:   "2026-04-15T10:00:00Z",
    shipping_passthrough: null,
  },
  {
    id:           "sample-invoice-2",
    code:         "INV-9002",
    order_id:     "sample-order-2",
    buyer_id:     "sample-buyer-2",
    buyer_name:   "Apex Retail Group",
    seller_id:    "sample-seller-1",
    seller_name:  "Sarah Chen Naturals",
    amount:       12_800,
    terms:        "net30",
    status:       "outstanding",
    due_date:     "2026-06-15T00:00:00Z",
    paid_at:      null,
    paid_method:  null,
    created_at:   "2026-05-16T10:00:00Z",
    shipping_passthrough: null,
  },
  {
    id:           "sample-invoice-3",
    code:         "INV-9003",
    order_id:     "sample-order-3",
    buyer_id:     "sample-buyer-1",
    buyer_name:   "Boutique Glow Co.",
    seller_id:    "sample-seller-1",
    seller_name:  "Sarah Chen Naturals",
    amount:       2_325,
    terms:        "prepaid",
    status:       "paid",
    due_date:     "2026-05-25T00:00:00Z",
    paid_at:      "2026-05-23T14:00:00Z",
    paid_method:  "wallet",
    created_at:   "2026-05-22T08:00:00Z",
    shipping_passthrough: {
      cost:          126.40,
      markup_pct:    15,
      markup_amount: 18.96,
      billed:        145.36,
      carrier:       "USPS",
      tracking:      "9405511899223334445566",
    },
  },
];


// ── Sample insurance covers (structural placeholders only — NO fake amounts)
//
// IMPORTANT compliance note: insurance coverage is an Allianz-issued legal
// product. We MUST NOT show fake dollar amounts because:
//   - A seller might think they have real coverage they don't
//   - Bank sponsors auditing our compliance flag synthetic insurance data
// So this preview only shows STRUCTURAL placeholders (em-dashes for amounts,
// "Sample" carrier label, % utilization but no $ amounts) — it teaches what
// the page looks like without implying any real coverage. ─────────────────
export const SAMPLE_INSURANCE_COVERS = [
  {
    buyer_id:        "sample-buyer-1",
    buyer_name:      "Boutique Glow Co.",
    buyer_email:     "ap@boutiqueglow.com",
    score:           738,
    carrier:         "Sample",       // intentionally NOT "Allianz Trade"
    source:          "covered",
    status_label:    "Covered",
    limit_display:   "—",            // structural placeholder, no fake $
    used_display:    "—",
    used_pct:        62,             // % only, no $ → safe to show
    premium_display: "—",
    expires_label:   "12 months from activation",
  },
  {
    buyer_id:        "sample-buyer-2",
    buyer_name:      "Apex Retail Group",
    buyer_email:     "finance@apexretail.com",
    score:           685,
    carrier:         "Sample",
    source:          "covered",
    status_label:    "Covered",
    limit_display:   "—",
    used_display:    "—",
    used_pct:        45,
    premium_display: "—",
    expires_label:   "12 months from activation",
  },
  {
    buyer_id:        "sample-buyer-3",
    buyer_name:      "Modern Mercantile",
    buyer_email:     "orders@modernmerc.com",
    score:           540,
    carrier:         "Sample",
    source:          "investigating",
    status_label:    "Underwriting in progress",
    limit_display:   "—",
    used_display:    "—",
    used_pct:        0,
    premium_display: "—",
    expires_label:   "—",
  },
];

// ── Sample shipments (linked to the orders above) ─────────────────────────
export const SAMPLE_SHIPMENTS = [
  {
    id:                "sample-shipment-1",
    order_id:          "sample-order-1",
    order_code:        "ORD-9001",
    buyer_id:          "sample-buyer-1",
    buyer_name:        "Boutique Glow Co.",
    seller_name:       "Sarah Chen Naturals",
    carrier:           "UPS",
    service:           "Ground",
    tracking_number:   "1Z999AA10123456784",
    tracking_url:      "https://www.ups.com/track?tracknum=1Z999AA10123456784",
    status:            "delivered",
    label_url:         null,
    cost:              360,
    weight:            18,
    delivered_at:      "2026-05-20T16:30:00Z",
    estimated_days:    3,
    origin_city:       "Brooklyn",
    destination_city:  "Manhattan",
    mode:              "parcel",
  },
  {
    id:                "sample-shipment-2",
    order_id:          "sample-order-3",
    order_code:        "ORD-9003",
    buyer_id:          "sample-buyer-1",
    buyer_name:        "Boutique Glow Co.",
    seller_name:       "Sarah Chen Naturals",
    carrier:           "USPS",
    service:           "Priority Mail",
    tracking_number:   "9405511899223334445566",
    tracking_url:      "https://tools.usps.com/go/TrackConfirmAction?tLabels=9405511899223334445566",
    status:            "in_transit",
    label_url:         null,
    cost:              126.40,
    weight:            8,
    delivered_at:      null,
    estimated_days:    2,
    origin_city:       "Portland",
    destination_city:  "Brooklyn",
    mode:              "parcel",
  },
];

// ── Sample activity timeline (used in 360 drawer) ──────────────────────────
export const SAMPLE_TIMELINE = [
  { at: "2d ago", type: "order_placed",  text: "Placed Order ORD-9001 · $4,200 · 240 units" },
  { at: "2d ago", type: "invoice_paid",  text: "Paid INV-9001 via ACH · $4,200" },
  { at: "5d ago", type: "credit_extended", text: "Credit limit increased $20K → $25K · Verqo Trade Score +12" },
  { at: "1w ago", type: "order_placed",  text: "Placed Order ORD-8732 · $3,180 · 184 units" },
  { at: "2w ago", type: "order_delivered", text: "ORD-8732 delivered · 2 days early" },
  { at: "3w ago", type: "invoice_paid",  text: "Paid INV-8732 · 5 days before due" },
  { at: "Aug '25", type: "connected",    text: "Connected to your wholesale portal" },
];

// ── Helpers ───────────────────────────────────────────────────────────────
export function isSampleId(id) {
  return typeof id === "string" && id.startsWith("sample-");
}

/** Format USD without bringing money() in as a dependency. */
export function sampleMoney(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2 }).format(n);
}
